/*
 * @Author: Ryan
 * @Date: 2026-01-30
 * @Description: AI UGC Video Generator 页面配置
 */
import { SEOHeadProps } from '../components/SEOHead';
import { FAQItem } from '../components/templates/FAQSection';
import { FeatureItem } from '../components/templates/FeaturesSection';
import { ReasonItem } from '../components/templates/WhyChooseSection';
import {
  createBreadcrumbSchema,
  createFAQSchema,
  createOrganizationSchema,
  createSoftwareAppSchema,
} from '../utils/structuredData';

/** 页面基础 URL */
const PAGE_URL = 'https://falcocut.ai/ai-ugc-video-generator';

/** FAQ Schema 数据（简化版，用于结构化数据） */
const FAQ_SCHEMA_DATA = [
  {
    question: 'What is an AI UGC video generator?',
    answer:
      'An AI UGC video generator is a platform that creates authentic, creator‑style videos using artificial intelligence instead of traditional filming. It transforms simple inputs—such as scripts, images, or prompts—into polished content that looks natural, relatable, and audience‑friendly.',
  },
  {
    question: 'What are UGC ads and how do they work?',
    answer:
      'UGC ads are promotional videos designed to look like genuine customer stories, giving brands a more relatable and human voice. They mimic everyday creator content, helping viewers feel a stronger connection to the message being shared.',
  },
  {
    question: 'Why should I use UGC video ads for my business?',
    answer:
      'UGC video ads help brands communicate in a more natural, relatable way that feels closer to real customer expression. They build trust by presenting product experiences through a conversational style that audiences instinctively respond to.',
  },
  {
    question: 'Are UGC ads more effective than traditional ads?',
    answer:
      'UGC ads often deliver stronger results because they feel spontaneous and relatable, making viewers more open to the message. They create a sense of everyday authenticity, while traditional ads rely on polished production that can feel distant.',
  },
  {
    question: 'Is it free to use FalcoCut AI UGC video generator?',
    answer:
      'FalcoCut offers a free tier that lets you explore essential tools and create a limited number of AI‑generated videos. This plan provides a small monthly credit allowance for understanding the workflow.',
  },
];

/** SEO 配置 */
export const SEO_CONFIG: SEOHeadProps = {
  title: 'AI UGC Video Generator - Create UGC Ads in Minutes | FalcoCut',
  description:
    'Create authentic, high-converting UGC ad videos in minutes with AI. Generate product videos, talking avatars, and social media content effortlessly.',
  url: PAGE_URL,
  image: 'https://falcocut.ai/images/og-img.png',
  imageAlt: 'FalcoCut AI UGC Video Generator',
  publishedTime: '2026-01-21',
  modifiedTime: '2026-01-30',
  structuredData: [
    createSoftwareAppSchema({
      name: 'FalcoCut AI UGC Video Generator',
      description:
        'Create authentic, high-converting UGC ad videos in minutes with AI. Generate product videos, talking avatars, and social media content effortlessly.',
      url: PAGE_URL,
      applicationCategory: 'MultimediaApplication',
      ratingValue: '4.9',
      ratingCount: '3200',
      featureList: [
        'AI-powered UGC video creation',
        'Product photo to video conversion',
        'Text to video generation',
        'AI avatar integration',
        'Multi-platform export',
        'Fast video rendering',
      ],
    }),
    createOrganizationSchema({
      name: 'FalcoCut',
      url: 'https://falcocut.ai',
      logo: 'https://falcocut.ai/images/logo.png',
    }),
    createFAQSchema({
      faqs: FAQ_SCHEMA_DATA,
    }),
    createBreadcrumbSchema({
      items: [
        { name: 'Home', url: 'https://falcocut.ai' },
        { name: 'AI UGC Video Generator', url: PAGE_URL },
      ],
    }),
  ],
};

/** Features Section 配置 */
export const FEATURES_CONFIG = {
  title: 'Powerful Features for Making Conversion‑Ready UGC Ads',
  subtitle:
    'FalcoCut is packed with all the tools you need to create top-performing UGC video ads quickly. From realistic AI avatars and natural voiceovers to fast video translation and scalable video creation, we make it easy to reach more customers and grow your business.',
  ctaText: 'Create UGC Videos',
  ctaUrl: '/ai-video-generator',
};

/** Features 列表 */
export const FEATURES_LIST: FeatureItem[] = [
  {
    title: 'Create Videos from Simple Text',
    description:
      'Bring your creative vision to life instantly just by typing a script. Instead of managing complex production schedules, marketing teams can easily generate spokesperson-style UGC ad videos where a lifelike AI Influencer delivers a perfect "Problem-Solution" pitch. Skipping the hours spent on filming and editing, FalcoCut can handle the visuals while you focus on your message. From simple testimonials to in‑depth product breakdowns, every script turns into a viral-ready video asset—no recording required. Whether testing a new "hook" or launching a global campaign, our text-to-video tool gets it done.',
    imageUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/create-videos-from-text.webp',
    imageAlt: 'Create UGC videos from simple text with AI',
    ctaText: 'Create UGC Videos',
  },
  {
    title: 'Animate Product Photos into Videos',
    description:
      'Transform static images into scroll-stopping ads that grab attention on busy feeds. Dropshippers and Shopify owners can quickly bridge the gap between a flat photo and a viral unboxing video that feels like it was filmed at home. This "raw" aesthetic helps small agencies build massive social proof and higher click rates without needing physical product samples or a camera crew. By breathing motion into simple product shots, FalcoCut fuels your TikTok ads, Instagram Reels, and Meta campaigns with fresh content that keeps your brand looking trendy. Effortlessly scale every new product launch with authentic-looking videos that capture the excitement of a real customer experience.',
    imageUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/animate-product-photos.webp',
    imageAlt: 'Animate product photos into UGC video ads',
    ctaText: 'Create UGC Videos',
  },
  {
    title: 'Grow Your Brand with AI Avatars',
    description:
      'Finding on‑camera talent can slow down your video plans, but FalcoCut\'s ready-to-go AI avatars keep production moving nonstop. Instead of hiring expensive models, store owners can pick a "virtual face" that customers recognize and trust in every post. These lifelike avatars help create friendly walk-and-talk UGC clips that feel like a chat with a neighbor rather than a sales pitch. From quick product intros to helpful talking-head guides, these digital actors give every video a warm, human touch without the stress of filming. Stop being a faceless company and start building a real bond with people who are ready to buy.',
    imageUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ai-avatars-ugc-video.webp',
    imageAlt: 'Grow your brand with AI avatars for UGC videos',
    ctaText: 'Create AI Avatars',
    ctaUrl: '/ai-avatar-generator',
  },
  {
    title: 'Instant Voiceovers and Auto-Subtitles',
    description:
      'Give your videos natural-sounding voiceovers and perfectly timed subtitles. FalcoCut delivers expressive, human-like voices that feel native to social feeds. By using advanced text-to-speech tool, brands can script two-person skit ads and conversational reaction clips that pull viewers into a story rather than just selling a product. Every word is paired with auto-synced subtitles, highlighting word-by-word to grab attention—even for viewers scrolling with the sound off. From fast-paced unboxing hooks to authentic product testimonials, the right tone and vibe are always just a click away. Let FalcoCut handle the narrative and delivery so every video feels polished, engaging, and ready to drive sales.',
    imageUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/instant-voiceovers-subtitles.webp',
    imageAlt: 'Instant voiceovers and auto-subtitles for UGC videos',
    ctaText: 'Add Voiceovers',
    ctaUrl: '/video-translator',
  },
  {
    title: 'Speak to the World in 30+ Languages',
    description:
      'Selling to the whole world is now easy by translating your UGC videos into over 30 different languages in seconds. Online sellers can take a single "POV" video or a "Must-Have" list and use AI dubbing to speak perfect Spanish, French, or Japanese. FalcoCut ensures the voices stay natural and friendly, helping small business owners connect with shoppers in other countries without paying for expensive translators. By turning a simple "Day in the Life" story into a global ad, DTC teams can make their brand a local favorite everywhere. Reaching new customers across the ocean is now as simple as clicking a button to share a message with the world.',
    imageUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/multilingual-ugc-video.webp',
    imageAlt: 'Translate UGC videos into 30+ languages',
    ctaText: 'Translate Videos',
    ctaUrl: '/video-translator',
  },
  {
    title: 'Easy Export for All Major Platforms',
    description:
      'Download your finished videos in the perfect size and format for every place you sell. These videos come ready to launch as high-performing ads on Shopify, Amazon, Meta, and TikTok right away. No need to worry about tech settings or resizing because FalcoCut automates all the formatting automatically. Launching campaigns faster and seeing results on all the top apps becomes the new reality for E-commerce marketers. This streamlined workflow makes it simple to test different versions across platforms to find which one sells best in record time. Save hours of work and stay organized by keeping every ad asset in one professional place.',
    imageUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/export-for-all-platforms.webp',
    imageAlt: 'Export UGC videos for all major platforms',
    ctaText: 'Create UGC Videos',
  },
];

/** Why Choose Section 配置 */
export const WHY_CHOOSE_CONFIG = {
  title: 'Why Choose Falcocut AI UGC Video Generator',
  subtitle:
    'FalcoCut helps you make real-looking video ads that people actually trust and like to watch. Grow your brand on social media with high-quality UGC-style content that turns viewers into happy customers.',
  ctaText: 'Create UGC Videos',
  ctaUrl: '/ai-video-generator',
};

/** Why Choose Reasons 列表 */
export const WHY_CHOOSE_REASONS: ReasonItem[] = [
  {
    title: 'Launch Ads Faster',
    description:
      "Don't wait weeks for someone to film and edit your content. Get your ads ready to post in just minutes so you can start selling immediately.",
  },
  {
    title: 'Reach More People Worldwide',
    description:
      'Make your UGC videos speak over 30 different languages with natural voices. Now you can sell your products all over the world without hiring translation experts.',
  },
  {
    title: 'No Video Editing Skills Required',
    description:
      "You don't need to know how to edit videos or use hard software. Our simple AI tool does the work for you so you can make a great video in just a few clicks.",
  },
  {
    title: 'Scale Without Limits',
    description:
      'Need dozens of different videos for a big campaign? Generate as many versions as you want in one afternoon while traditional creators are still setting up.',
  },
  {
    title: 'Always On-Brand',
    description:
      'Keep a consistent look and voice across every single video you make. Your AI actors are always available and always deliver the perfect message for your store.',
  },
  {
    title: 'Save Money on Production',
    description:
      'Stop paying thousands of dollars for expensive actors and camera crews. Create professional UGC video ads for a much lower price.',
  },
];

/** FAQ Section 配置 */
export const FAQ_CONFIG = {
  title: 'Frequently Asked Questions',
};

/** FAQ 列表 */
export const FAQ_LIST: FAQItem[] = [
  {
    question: 'What is an AI UGC video generator?',
    answer:
      'An AI UGC video generator is a platform that creates authentic, creator‑style videos using artificial intelligence instead of traditional filming. It transforms simple inputs—such as scripts, images, or prompts—into polished content that looks natural, relatable, and audience‑friendly. The tool replicates the tone and spontaneity of real user videos while giving you full control over messaging and style. Brands can scale consistent, high‑quality UGC content quickly without relying on influencers, studios, or complex production workflows.',
  },
  {
    question: 'What are UGC ads and how do they work?',
    answer:
      'UGC ads are promotional videos designed to look like genuine customer stories, giving brands a more relatable and human voice. They mimic everyday creator content, helping viewers feel a stronger connection to the message being shared. These ads work by blending natural delivery with clear product highlights, making the promotion feel effortless rather than overly produced. By combining authenticity with strategic storytelling, they encourage audiences to trust the brand and engage more meaningfully with the content.',
  },
  {
    question: 'Why should I use UGC video ads for my business?',
    answer:
      'UGC video ads help brands communicate in a more natural, relatable way that feels closer to real customer expression. They build trust by presenting product experiences through a conversational style that audiences instinctively respond to and remember. This format often encourages stronger engagement because viewers perceive the content as genuine rather than overly polished or scripted. By using UGC‑style videos, businesses can deliver persuasive messages that resonate across diverse channels while maintaining an approachable brand presence.',
  },
  {
    question: 'Are UGC ads more effective than traditional ads?',
    answer:
      'UGC ads often deliver stronger results because they feel spontaneous and relatable, making viewers more open to the message. They create a sense of everyday authenticity, while traditional ads rely on polished production that can feel distant or overly curated. UGC formats adapt quickly to fast‑moving platforms, whereas conventional campaigns usually require longer timelines and higher budgets. This contrast makes UGC a compelling choice for brands seeking responsive, trust‑driven communication that resonates across modern channels.',
  },
  {
    question: 'How can I create UGC ads with avatars?',
    listIntro:
      'When satisfied, export the finished UGC ad and deploy it across your marketing platforms with consistent, polished presentation.',
    orderedList: [
      {
        content:
          "Select an AI avatar from FalcoCut's collection, choosing a look and presence that aligns with your brand message.",
      },
      {
        content:
          'Upload your product visuals so the platform can incorporate them directly into the creative workflow.',
      },
      {
        content:
          'Craft your script and then adjust the video format, pacing, and voice style to ensure the final output suits your preferred channels.',
      },
      {
        content:
          'Once everything is set, generate the video and review the preview to confirm it reflects your intended narrative and style.',
      },
    ],
  },
  {
    question: 'Can I upload product images or reference videos?',
    answer:
      'FalcoCut allows you to add product photos directly, giving the system clear visuals to incorporate into your UGC content. These images help our AI present your item accurately and maintain a cohesive look throughout the final video. Currently, FalcoCut does not accept reference videos, so creative direction should be provided through images or written guidance. This setup keeps the workflow streamlined while still giving you precise influence over how your product is showcased.',
  },
  {
    question: 'Can I create testimonial‑style or TikTok‑style videos?',
    answer:
      'Yes, FalcoCut lets you produce testimonial‑style videos that feel expressive and grounded in real customer perspectives. It also supports TikTok‑style formats with quick pacing and layouts designed for short‑form social platforms. You can shape each style through your script, avatar selection, and creative adjustments to match your campaign goals. This flexibility helps your brand deliver engaging visuals that align with the expectations of modern digital audiences.',
  },
  {
    question: 'Do I need to film or record anything for UGC ads?',
    answer:
      "No, you don't need to capture any footage or audio to produce UGC‑style videos with FalcoCut. The platform generates polished creator‑style content entirely from your script, visuals, and chosen avatar, removing the need for cameras or recording setups. This approach streamlines production by replacing traditional shoots with AI‑driven creation that still feels natural and expressive. It allows your team to deliver consistent, high‑quality UGC ads without coordinating equipment, talent, or on‑site logistics.",
  },
  {
    question: 'Can I produce AI UGC videos in different languages?',
    answer:
      'Yes, FalcoCut supports the creation of AI UGC videos in over 30 languages, enabling broad international reach. The system delivers smooth lip‑sync and natural expression, allowing each language to feel authentic to its intended audience. You can shape the delivery style for every market, adjusting tone and emphasis to match regional communication preferences. This multilingual capability helps your brand maintain consistent storytelling while speaking directly to customers in their native language.',
  },
  {
    question: 'What should I do if my UGC video is taking too long to generate?',
    answer:
      'If your video seems unusually slow, try refreshing the project page to reestablish a stable connection with the system. You can also shorten the script or simplify certain elements, which often helps the rendering process move more efficiently. Closing other heavy applications or switching to a stronger network may improve overall performance during generation. If delays continue beyond typical processing times, reaching out to support ensures your project is reviewed and resolved promptly.',
  },
  {
    question: 'Is it free to use FalcoCut AI UGC video generator?',
    answer:
      'FalcoCut offers a free tier that lets you explore essential tools and create a limited number of AI‑generated videos. This plan provides a small monthly credit allowance, giving you enough access to understand the workflow and overall experience. If you require additional credits, higher output volume, or premium capabilities, upgrading to a paid plan becomes necessary. The subscription options unlock expanded resources and advanced functions designed for users who need more consistent production capacity.',
  },
];

/** Footer CTA Section 配置 */
export const FOOTER_CTA_CONFIG = {
  title: 'Make Top-Selling UGC Videos with AI',
  subtitle:
    'Create great-looking videos that people love to watch and share. Just pick your favorite AI actor and let FalcoCut build ads that get more views and customers for your business.',
  ctaText: 'Create UGC Videos Now',
  ctaUrl: '/ai-video-generator',
};
